import debug from "debug";
import * as THREE from "three";

const log = debug("app:gsg");



/**
 * Singleton class responsible for managing and tracking connection points (ports) in a 3D scene.
 * @category Experience
 * @subcategory Utils
 */
class PortVault {
  static instance = null;

  /**
   * Constructs a new PortVault instance or returns the existing singleton instance.
   * @param {Object} experience - The main experience object containing the scene and event hub.
   */
  constructor(experience) {
    if (PortVault.instance) {
      return PortVault.instance;
    }
    this.experience = experience;
    this.scene = this.experience.scene;
    this.eventHub = this.experience.eventHub;
    this.portVaultMap = new Map();

    this.init();
    PortVault.instance = this;
  }

  cleanup() {
    try {
      // Remove all event listeners to prevent memory leaks
      this.eventHub.off("xmd", this.init.bind(this));

      // Reset colors of all connection points before clearing the vault
      this.resetColors();

      // Clear the port vault map
      this.portVaultMap.clear();

      log("PortVault cleanup complete: Event listeners removed, colors reset, and vault cleared.");
    } catch (error) {
      log("Error during PortVault cleanup:", error);
    }
  }

  /**
   * Retrieves the singleton instance of PortVault, creating it if necessary.
   * @param {Object} experience - The main experience object.
   * @returns {PortVault} The singleton instance.
   */
  static getInstance(experience) {
    if (!PortVault.instance) {
      PortVault.instance = new PortVault(experience);
    }
    return PortVault.instance;
  }

  /**
   * Initializes the connection point vault, setting up event listeners and processing connection points.
   */
  init() {
    log("initialized ConnPointVault");
    this.eventHub.on("xmd", () => {
      try {
        this.resetColors(); // Reset colors first
        this.portVaultMap.clear(); // Clear the vault
        const connPointsArray = this.collectConnectionPoints();
        log("connection points", connPointsArray);
        const positionArray = this.captureWorldCoordinates(connPointsArray);
        const rotationArray = this.addConnectionPointAngles(positionArray);
        this.buildConnectionVault(rotationArray);
        log("vault", this.portVaultMap);
        this.applyConnections(); // Change color here after all checks
      } catch (error) {
        log("Error during init process:", error);
      }
    });
  }

  /**
   * Resets the colors of all connection points to their original state.
   */
  resetColors() {
    try {
      this.portVaultMap.forEach(({ port1, port2 }) => {
        this.resetConnPoint(port1.group);
        this.resetConnPoint(port2.group);
      });
      this.portVaultMap.clear(); // Clear the map after resetting
    } catch (error) {
      log("Error resetting colors:", error);
    }
  }

  /**
   * Resets the color of a specific connection point group to its original color.
   * @param {THREE.Group} group - The group representing the connection point.
   */
  resetConnPoint(group) {
    try {
      const circleChild = group.children.find(
        (child) => child.name === "circle"
      );
      if (circleChild) {
        const originalColor = circleChild.userData.originalColor;
        if (originalColor) {
          circleChild.material.color.set(originalColor);
        }
      }
    } catch (error) {
      log("Error resetting connection point:", error);
    }
  }

  /**
   * Collects all connection points in the scene.
   * @returns {Array<Object>} An array of connection point objects with group and type information.
   */
  collectConnectionPoints() {
    try {
      const connPointsArray = [];
      this.scene.traverse((object) => {
        if (object.isGroup && object.name === "ConnPoint") {
          connPointsArray.push({
            group: object,
            type: object.userData.typeOfPort,
          });
        }
      });
      return connPointsArray;
    } catch (error) {
      log("Error collecting connection points:", error);
      return [];
    }
  }

  /**
   * Captures the world coordinates of each connection point.
   * @param {Array<Object>} connPointsArray - The array of connection point objects.
   * @returns {Array<Object>} An array of connection points with world coordinates and position keys.
   */
  captureWorldCoordinates(connPointsArray) {
    try {
      return connPointsArray.map((connPoint) => {
        const worldPosition = new THREE.Vector3();

        const circleChild = connPoint.group.children.find(
          (child) => child.name === "circle"
        );
        if (circleChild) {
          circleChild.getWorldPosition(worldPosition);
        } else {
          log(`Circle child not found for ${connPoint.group.name}`);
        }

        return {
          ...connPoint,
          x: Math.round(worldPosition.x),
          z: Math.round(worldPosition.z),
          positionKey: `${Math.round(worldPosition.x)}_${Math.round(
            worldPosition.z
          )}`,
          parent: circleChild.parent.parent.userData.pn,
        };
      });
    } catch (error) {
      log("Error capturing world coordinates:", error);
      return [];
    }
  }

  /**
   * Adds rotation angles to each connection point based on the parent's world quaternion.
   * @param {Array<Object>} connPointsArray - The array of connection point objects.
   * @returns {Array<Object>} An array of connection points with rotation angles.
   */
  addConnectionPointAngles(connPointsArray) {
    try {
      return connPointsArray.map((connPoint) => {
        const parent = connPoint.group.parent; // Get the parent of the connection point

        // Get the parent's world quaternion
        const worldQuaternion = new THREE.Quaternion();
        parent.getWorldQuaternion(worldQuaternion);

        // Convert the quaternion to Euler angles
        const euler = new THREE.Euler().setFromQuaternion(
          worldQuaternion,
          "YXZ"
        );

        // Extract and normalize the Y rotation angle
        let angle = THREE.MathUtils.radToDeg(euler.y);
        angle = Math.round(angle / 90) * 90; // Round to the nearest 90 degrees

        // Ensure angle is within [0, 90, 180, 270]
        angle = (angle + 360) % 360;

        return {
          ...connPoint,
          angle, // Add the calculated absolute angle to the connection point
        };
      });
    } catch (error) {
      log("Error adding connection point angles:", error);
      return [];
    }
  }

  /**
   * Builds the connection vault by linking compatible connection points based on position and type.
   * @param {Array<Object>} positionArray - The array of connection points with position keys.
   */
  buildConnectionVault(positionArray) {
    try {
      positionArray.forEach((port1, index1) => {
        for (let index2 = index1 + 1; index2 < positionArray.length; index2++) {
          const port2 = positionArray[index2];
          if (
            port1.positionKey === port2.positionKey &&
            this.canConnect(port1.type, port2.type)
          ) {
            this.portVaultMap.set(port1.positionKey, {
              port1,
              port2,
            });
          }
        }
      });
    } catch (error) {
      log("Error building connection vault:", error);
    }
  }

  /**
   * Determines whether two connection points can be connected based on their types.
   * @param {string} type1 - The type of the first connection point.
   * @param {string} type2 - The type of the second connection point.
   * @returns {boolean} True if the points can connect, otherwise false.
   */
  canConnect(type1, type2) {
    try {
      // Always prevent connection if type1 or type2 is "PROBE"
      if (type1 === "PROBE" || type2 === "PROBE") {
        return false;
      }

      this.connectionCheckEnabled = false;
      if (!this.connectionCheckEnabled) {
        return true;
      }

      return !(type1 === type2 && (type1 === "INPUT" || type1 === "OUTPUT"));
    } catch (error) {
      log("Error in connection check:", error);
      return false;
    }
  }

  /**
   * Applies visual feedback to connected points by changing their colors.
   */
  applyConnections() {
    try {
      this.portVaultMap.forEach(({ port1, port2 }) => {
        this.changeCircleColor(port1.group, 0x00ff00);
        this.changeCircleColor(port2.group, 0x00ff00);
      });
    } catch (error) {
      log("Error applying connections:", error);
    }
  }

  /**
   * Changes the color of the circle geometry in a connection point group.
   * @param {THREE.Group} group - The group representing the connection point.
   * @param {number} color - The new color to apply.
   */
  changeCircleColor(group, color) {
    try {
      group.children.forEach((child) => {
        if (child.isMesh && child.geometry.type === "CircleGeometry") {
          // Save original color if not already saved
          if (!child.userData.originalColor) {
            child.userData.originalColor = child.material.color.clone(); // Store as a THREE.Color object
            log("original color", child.userData.originalColor.getHex()); // Log the color for debugging
          }
          // Apply the new color
          child.material.color.set(color);
        }
      });
    } catch (error) {
      log("Error changing circle color:", error);
    }
  }
}

export default PortVault;
